import { useState, useRef, useEffect, useCallback } from 'react';
import { Play, Pause, Volume2 } from 'lucide-react';

export function Metronome({ bpm = 60 }) {
  const [isPlaying, setIsPlaying] = useState(false);
  const audioContextRef = useRef(null);
  const nextNoteTimeRef = useRef(0);
  const timerIdRef = useRef(null);

  // Create audio context on first interaction
  const initAudioContext = useCallback(() => {
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    return audioContextRef.current;
  }, []);

  // Play a single click sound
  const playClick = useCallback((time) => {
    const audioContext = audioContextRef.current;
    if (!audioContext) return;

    // Create oscillator for click sound
    const osc = audioContext.createOscillator();
    const gainNode = audioContext.createGain();

    osc.connect(gainNode);
    gainNode.connect(audioContext.destination);

    // Short, punchy click sound
    osc.frequency.value = 1000;
    osc.type = 'sine';

    // Quick attack and decay
    gainNode.gain.setValueAtTime(0, time);
    gainNode.gain.linearRampToValueAtTime(0.5, time + 0.001);
    gainNode.gain.linearRampToValueAtTime(0, time + 0.05);

    osc.start(time);
    osc.stop(time + 0.05);
  }, []);

  // Scheduler for precise timing
  const scheduler = useCallback(() => {
    const audioContext = audioContextRef.current;
    if (!audioContext) return;

    const secondsPerBeat = 60.0 / bpm;
    const scheduleAheadTime = 0.1; // Schedule 100ms ahead

    while (nextNoteTimeRef.current < audioContext.currentTime + scheduleAheadTime) {
      playClick(nextNoteTimeRef.current);
      nextNoteTimeRef.current += secondsPerBeat;
    }

    timerIdRef.current = requestAnimationFrame(scheduler);
  }, [bpm, playClick]);

  // Start metronome
  const start = useCallback(() => {
    const audioContext = initAudioContext();
    
    // Resume audio context if suspended (browser autoplay policy)
    if (audioContext.state === 'suspended') {
      audioContext.resume();
    }

    nextNoteTimeRef.current = audioContext.currentTime;
    scheduler();
    setIsPlaying(true);
  }, [initAudioContext, scheduler]);

  // Stop metronome
  const stop = useCallback(() => {
    if (timerIdRef.current) {
      cancelAnimationFrame(timerIdRef.current);
      timerIdRef.current = null;
    }
    setIsPlaying(false);
  }, []);

  // Toggle play/pause
  const toggle = useCallback(() => {
    if (isPlaying) {
      stop();
    } else {
      start();
    }
  }, [isPlaying, start, stop]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stop();
      if (audioContextRef.current) {
        audioContextRef.current.close();
        audioContextRef.current = null;
      }
    };
  }, [stop]);

  return (
    <button
      onClick={toggle}
      className={`
        flex items-center gap-2 px-4 py-2 rounded-lg border transition-all
        ${isPlaying 
          ? 'bg-primary text-white border-primary' 
          : 'bg-white text-gray-700 border-gray-300 hover:border-gray-400'
        }
      `}
    >
      {isPlaying ? (
        <>
          <Pause className="h-4 w-4" />
          <span className="text-sm font-medium">Stop Metronome</span>
        </>
      ) : (
        <>
          <Volume2 className="h-4 w-4" />
          <span className="text-sm font-medium">{bpm} BPM Metronome</span>
        </>
      )}
    </button>
  );
}